import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import { isDisposableEmail } from 'disposable-email-domains-js';
import {
  buscaLocais,
  buscaZonas,
  buscaBairros,
  votacao
} from '../services/Votacao';

import { useGlobal } from '../contexts/global';
import API from '../plugins/axios';
import { getImagem } from '../utils/imagens';
import { getErrorMessage } from '../utils/errorHandler';
import cidadeSP from '../data/cidades/sao-paulo.json';
import cidadeBH from '../data/cidades/belo-horizonte.json';
import '../assets/scss/components/VoteForm.scss';

import bgVoteform from '../assets/images/bg_voteform.png';
import iconSearch from '../assets/images/icon_search.png';
import iconLocation from '../assets/images/icon_location.png';
import logoGoogle from '../assets/images/logo_google.svg';

let timeout;

const getTemas = () => {
  const activePage = sessionStorage.getItem('cidadeAtivaPage') || '';
  const cidadeAtiva = activePage.includes('belo-horizonte')
    ? cidadeBH
    : cidadeSP;

  const temasObj = {};
  cidadeAtiva.categorias.forEach((cat, index) => {
    const key = cat.slug.replaceAll('-', '_');
    temasObj[key] = {
      id: cat.id || index + 1,
      nome: cat.nome,
      image: getImagem(cat.imagem)
    };
  });

  return {
    temas: temasObj,
    maxCategorias: cidadeAtiva.categorias.length,
    cidadeAtiva
  };
};

const VoteForm = () => {
  const { temas, maxCategorias, cidadeAtiva } = getTemas();
  const defaultCenter = cidadeAtiva.coordenadas || {
    lat: -23.554438,
    lng: -46.649014
  };
  const [step, setStep] = useState(1);
  const [inputLocal, setInputLocal] = useState('');
  const [showCadastroPadaria, setShowCadastroPadaria] = useState(false);
  const [isLoading, setIsLoadinng] = useState(false);
  const [checkRegulamento1, setCheckRegulamento1] = useState(
    localStorage.getItem('user_accepted_terms') === 'true'
  );
  const [checkRegulamento2, setCheckRegulamento2] = useState(
    localStorage.getItem('user_accepted_terms') === 'true'
  );
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [nomePadaria, setNomePadaria] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('user_email') || '');
  const [zona, setZona] = useState('');
  const [bairro, setBairro] = useState('');
  const [zonasOptions, setZonasOptions] = useState([]);
  const [bairrosOptions, setBairrosOptions] = useState([]);

  const {
    selectedLocation,
    setSelectedLocation,
    setCenter,
    setZoom,
    setShowRegulamento
  } = useGlobal();

  const history = useHistory();
  const { tema } = useParams();
  const temaId = temas[tema.replaceAll('-', '_')].id;

  const handleShowCadastroPadaria = () => {
    setInputLocal('');
    setSelectedLocation('');
    setShowCadastroPadaria(true);
  };

  const hideCadastroPadocaria = () => {
    setNomePadaria('');
    setZona('');
    setBairro('');
    setShowCadastroPadaria(false);
  };

  const handleSelectLocation = (local) => {
    hideCadastroPadocaria();
    setInputLocal(local.nome);

    if (
      local.localizacao.latitude === null ||
      local.localizacao.longitude === null
    ) {
      setCenter(defaultCenter);
    } else {
      setCenter({
        lat: local.localizacao.latitude,
        lng: local.localizacao.longitude
      });
    }
    setZoom(18);
    setSelectedLocation(local);

    if (local.bairro_id !== null) {
      setBairro(local.bairro_id);
      setZona(local.zona_id);
    }

    setResultadosBusca([]);
  };

  const handleAutocomplete = (e) => {
    setSelectedLocation('');
    setInputLocal(e.target.value);
    if (e.target.value.length >= 3) {
      setLoadingAutocomplete(true);
      clearTimeout(timeout);
      timeout = setTimeout(async () => {
        const resp = await buscaLocais(e.target.value, temaId);
        setResultadosBusca(resp.data);
        setLoadingAutocomplete(false);
      }, 500);
    } else if (e.target.value.length < 3) {
      setResultadosBusca([]);
      setLoadingAutocomplete(false);
    }
  };

  const handleSelectZona = async (e) => {
    setZona(e.target.value);

    const resp = await buscaBairros(e.target.value);
    setBairrosOptions(resp);
  };

  const validateEmail = (emailInput) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailInput)) {
      return 'Por favor, insira um formato de e-mail válido.';
    }

    if (isDisposableEmail(emailInput)) {
      return 'E-mails temporários não são permitidos nesta votação. Por favor, use um e-mail válido.';
    }

    return null;
  };

  const handleVotacao = (ssoToken = null, social = '') => {
    if (email && !ssoToken) {
      localStorage.setItem('user_email', email);
    }
    localStorage.setItem('user_accepted_terms', 'true');
    setIsLoadinng(true);
    const grecaptcha = window.grecaptcha?.enterprise || window.grecaptcha;
    grecaptcha.ready(() => {
      grecaptcha
        .execute(process.env.REACT_APP_KEY_RECAPTCHA, {
          action: 'submit'
        })
        .then(async (token) => {
          const data = {
            email: email || '',
            token_grecaptcha: token,
            tema_id: temas[tema.replaceAll('-', '_')].id,
            zona_id: zona || '',
            bairro_id: bairro || '',
            endereco_completo: selectedLocation
              ? selectedLocation.endereco_completo
              : '',
            latitude: selectedLocation
              ? selectedLocation.localizacao.latitude
              : '',
            longitude: selectedLocation
              ? selectedLocation.localizacao.longitude
              : '',
            estabelecimento: selectedLocation
              ? selectedLocation.nome
              : nomePadaria,
            place_id: selectedLocation ? selectedLocation.place_id : ''
          };

          if (ssoToken) {
            data[social] = true;
          }

          try {
            const response = await votacao(data, ssoToken || '');
            const { categories } = response.data;

            const votes = categories;
            const savedCityPage =
              sessionStorage.getItem('cidadeAtivaPage') || '/';
            let urlRedirect = savedCityPage;
            let textRedirect = ssoToken
              ? 'Obrigado por votar nessa categoria!'
              : 'Em alguns instantes você receberá um e-mail para confirmar seu voto';
            let buttonText = 'Voltar para home';

            const idNextTema = [...Array(maxCategorias).keys()]
              .map((i) => i + 1)
              .find((i) => !votes.includes(i));

            if (votes.length < maxCategorias) {
              const temaEncontrado = Object.entries(temas).find(
                ([key, t]) => t.id === idNextTema && key != null
              );

              if (temaEncontrado) {
                const [key, t] = temaEncontrado;
                buttonText = t.nome;
                urlRedirect = key;
                textRedirect = ssoToken
                  ? 'Obrigado por votar nessa categoria! Sinta-se à vontade para votar também na categoria:'
                  : 'Você receberá um e-mail para confirmar seu voto em breve. Enquanto isso, sinta-se à vontade para votar também na categoria:';
              }
            }

            if (votes.length === maxCategorias) {
              textRedirect =
                'Obrigado por votar em todas as categorias! Sua participação é muito importante para nós.';
              buttonText = 'Voltar para home';
              urlRedirect = savedCityPage;
            }

            setIsLoadinng(false);

            Swal.fire({
              icon: ssoToken ? 'success' : 'warning',
              text: textRedirect,
              confirmButtonText: buttonText
            }).then(() => {
              setStep(1);
              setInputLocal('');
              setSelectedLocation('');
              history.push(urlRedirect);
            });
          } catch (error) {
            setIsLoadinng(false);

            if (ssoToken && error.response?.status === 401) {
              sessionStorage.removeItem(`${social}_access_token`);
              // eslint-disable-next-line no-use-before-define
              handleClickSSO(social, true);
              return;
            }

            if (error.response?.status === 422) {
              const errorMsg = getErrorMessage(
                error,
                'Usuário já votou nessa categoria!'
              );
              Swal.fire({
                timer: 3500,
                icon: 'error',
                showConfirmButton: false,
                text: errorMsg
              });
            } else {
              const errorMsg = getErrorMessage(
                error,
                'Ocorreu um erro inesperado. Tente novamente mais tarde.'
              );
              Swal.fire({
                timer: 3500,
                icon: 'error',
                showConfirmButton: false,
                text: errorMsg
              });
            }
          }
        });
    });
  };

  const handleClickSSO = (social, forceRedirect = false) => {
    const data = {
      email: email || '',
      tema_id: temas[tema.replaceAll('-', '_')].id,
      zona_id: zona || '',
      bairro_id: bairro || '',
      endereco_completo: selectedLocation
        ? selectedLocation.endereco_completo
        : '',
      latitude: selectedLocation ? selectedLocation.localizacao.latitude : '',
      longitude: selectedLocation ? selectedLocation.localizacao.longitude : '',
      estabelecimento: selectedLocation ? selectedLocation.nome : nomePadaria,
      place_id: selectedLocation ? selectedLocation.place_id : ''
    };

    const savedToken = sessionStorage.getItem(`${social}_access_token`);

    if (savedToken && !forceRedirect) {
      handleVotacao(savedToken, social);
      return;
    }

    localStorage.setItem('user_accepted_terms', 'true');
    localStorage.setItem('vote_data', JSON.stringify(data));

    if (social === 'google') {
      window.location.href = `${API.defaults.baseURL}auth/login/google`;
    } else if (social === 'facebook') {
      window.location.href = `${API.defaults.baseURL}auth/login/facebook`;
    }
  };

  useEffect(() => {
    let unmounted = false;

    if (temaId === 8) {
      const loadBairros = async () => {
        const resp = await buscaBairros(7);
        if (!unmounted) {
          setBairrosOptions(resp);
        }
      };
      setZona(7);
      loadBairros();
    } else {
      const loadZonas = async () => {
        const resp = await buscaZonas();
        if (!unmounted) {
          setZonasOptions(resp);
        }
      };
      loadZonas();
    }

    setSelectedLocation('');
    setCenter(defaultCenter);
    setZoom(15);
    return () => {
      unmounted = true;
    };
  }, []);

  useEffect(() => {
    const loadScriptByURL = (id, url, callback) => {
      const isScriptExist = document.getElementById(id);

      if (!isScriptExist) {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = url;
        script.id = id;
        script.onload = () => {
          if (callback) callback();
        };
        document.body.appendChild(script);
      }

      if (isScriptExist && callback) callback();
    };

    // load the script by passing the URL
    loadScriptByURL(
      'recaptcha-key',
      `https://www.google.com/recaptcha/enterprise.js?render=${process.env.REACT_APP_KEY_RECAPTCHA}`,
      () => {}
    );
  }, []);

  return (
    <div className="in-voteform">
      <div className="in-voteform-bg">
        <img src={bgVoteform} alt={bgVoteform} />
      </div>
      <div className="in-voteform-grid">
        <div className="in-voteform-grid-sup">
          <div className="in-voteform-grid-sup-lf">
            <img
              src={temas[tema.replaceAll('-', '_')].image}
              alt={temas[tema.replaceAll('-', '_')].image}
            />
          </div>
          <div className="in-voteform-grid-sup-rt">
            <div className="in-voteform-grid-sup-rt-ct">
              <p>Votar na categoria</p>
              <p>{temas[tema.replaceAll('-', '_')].nome}</p>
            </div>
          </div>
        </div>
        <div className="in-voteform-grid-sub">
          {step === 1 && (
            <>
              {/* Área de busca de padarias */}
              <div className="in-voteform-grid-sub-search">
                <div className="in-voteform-grid-sub-search-input">
                  <input
                    type="text"
                    value={inputLocal}
                    onChange={handleAutocomplete}
                    placeholder="Pesquise padarias"
                  />
                  {loadingAutocomplete ? (
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                  ) : (
                    <img src={iconSearch} alt={iconSearch} />
                  )}
                </div>
                <div id="checkbox-wrapper-1" style={{ padding: '5px' }}>
                  <input
                    type="checkbox"
                    name="chk-regulamento-1"
                    id="chk-regulamento-1"
                    checked={checkRegulamento1}
                    onChange={() => setCheckRegulamento1(!checkRegulamento1)}
                  />{' '}
                  <label htmlFor="chk-regulamento-1">
                    Li e concordo com o{' '}
                    <span
                      style={{
                        textDecoration: 'underline',
                        cursor: 'pointer'
                      }}
                      onClick={() => setShowRegulamento(true)}
                    >
                      regulamento
                    </span>
                  </label>
                </div>
                <div className="in-voteform-grid-sub-search-btn">
                  <button
                    disabled={selectedLocation === '' || !checkRegulamento1}
                    onClick={() => setStep(2)}
                    type="button"
                  >
                    Confirmar
                  </button>
                </div>
                <div className="in-voteform-grid-sub-search-ancor">
                  <p>
                    Caso não encontre a padaria,{' '}
                    <span onClick={handleShowCadastroPadaria}>clique aqui</span>
                  </p>
                </div>

                {/* Resultados do autocomplete */}
                {resultadosBusca.length > 0 && (
                  <div className="in-voteform-grid-sub-search-results">
                    <div className="in-voteform-grid-sub-search-results-scrollbar">
                      <ul>
                        {resultadosBusca.map((item) => (
                          <li
                            key={item.key}
                            onClick={() => handleSelectLocation(item)}
                          >
                            <img src={iconLocation} alt={iconLocation} />
                            <span>
                              {item.nome}
                              {item.endereco_completo ? <br /> : null}
                              {item.endereco_completo ? (
                                <small>{`${item.endereco_completo}`}</small>
                              ) : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {resultadosBusca.length < 1 &&
                  inputLocal.length >= 3 &&
                  !loadingAutocomplete &&
                  !selectedLocation && (
                    <div className="in-voteform-grid-sub-search-results">
                      <div className="in-voteform-grid-sub-search-results-scrollbar">
                        <ul>
                          <li className="no-result">
                            <span>Nenhum resultado encontrado!</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
              </div>

              {/* Cadastro de Padaria */}
              {showCadastroPadaria && (
                <div className="in-voteform-grid-sub-register">
                  <div className="in-voteform-grid-sub-register-title">
                    <p>Cadastrar padaria</p>
                  </div>
                  <div className="in-voteform-grid-sub-register-form">
                    <input
                      type="text"
                      id="nome-padaria"
                      name="nome-padaria"
                      placeholder="Digite o nome da padaria"
                      value={nomePadaria}
                      onChange={(e) => setNomePadaria(e.target.value)}
                    />
                    {temaId !== 8 && (
                      <select
                        value={zona}
                        id="slt-zonas"
                        name="slt-zonas"
                        onChange={handleSelectZona}
                      >
                        <option value="">Zona</option>
                        {zonasOptions.map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.zona}
                          </option>
                        ))}
                      </select>
                    )}

                    <select
                      value={bairro}
                      id="slt-bairros"
                      className="input"
                      name="slt-bairros"
                      onChange={(e) => setBairro(e.target.value)}
                    >
                      {bairrosOptions.length > 0 ? (
                        <option value="">Selecione o bairro...</option>
                      ) : (
                        <option value="">Selecione uma zona acima</option>
                      )}
                      {bairrosOptions.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.bairro}
                        </option>
                      ))}
                    </select>
                    <div id="checkbox-wrapper-2" style={{ padding: '5px' }}>
                      <input
                        type="checkbox"
                        name="chk-regulamento-2"
                        id="chk-regulamento-2"
                        checked={checkRegulamento2}
                        onChange={() =>
                          setCheckRegulamento2(!checkRegulamento2)
                        }
                      />{' '}
                      <label htmlFor="chk-regulamento-2">
                        Li e concordo com o{' '}
                        <span
                          style={{
                            textDecoration: 'underline',
                            cursor: 'pointer'
                          }}
                          onClick={() => setShowRegulamento(true)}
                        >
                          regulamento
                        </span>
                      </label>
                    </div>
                    <button
                      disabled={
                        nomePadaria === '' ||
                        zona === '' ||
                        bairro === '' ||
                        !checkRegulamento2
                      }
                      type="button"
                      onClick={() => setStep(2)}
                    >
                      Cadastrar
                    </button>
                  </div>
                  <div className="in-voteform-grid-sub-register-back">
                    <span onClick={hideCadastroPadocaria}>Voltar</span>
                  </div>
                </div>
              )}
            </>
          )}
          {step === 2 && (
            <>
              <div className="in-voteform-grid-sub-confirm">
                <div className="in-voteform-grid-sub-confirm-title">
                  <p>Confirmar voto em:</p>
                  <p>{selectedLocation ? inputLocal : nomePadaria}</p>
                  <p>
                    Para finalizar o voto precisamos fazer uma confirmação por
                    e-mail
                  </p>
                </div>
                <div className="in-voteform-grid-sub-confirm-email">
                  <div className="in-voteform-grid-sub-confirm-email-input">
                    <input
                      id="email"
                      type="email"
                      name="email"
                      value={email}
                      placeholder="Digite o seu e-mail"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <button
                      disabled={email === '' || isLoading}
                      type="button"
                      onClick={() => {
                        const errorMessage = validateEmail(email);

                        if (errorMessage) {
                          Swal.fire({
                            icon: 'error',
                            title: 'E-mail inválido',
                            text: errorMessage,
                            confirmButtonText: 'Entendi'
                          });
                          return;
                        }

                        setIsLoadinng(true);
                        setTimeout(() => {
                          handleVotacao();
                        }, 2000);
                      }}
                    >
                      {isLoading ? (
                        <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
                      ) : (
                        'Confirmar'
                      )}
                    </button>
                  </div>

                  <div className="in-voteform-grid-sub-confirm-email-or">
                    <div />
                    <span>ou</span>
                    <div />
                  </div>

                  <div className="in-voteform-grid-sub-confirm-email-btn">
                    <button
                      type="button"
                      onClick={() => handleClickSSO('google')}
                    >
                      <img src={logoGoogle} alt="Google logo" />
                      <span>Confirmar voto com o Google</span>
                    </button>
                  </div>
                  <div className="in-voteform-grid-sub-confirm-email-back">
                    <span onClick={() => setStep(1)}>Voltar</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoteForm;
